import { DialogComponent } from '@theme/dialog';

/**
 * Klaviyo's client-side back-in-stock endpoint. Public key only — it takes the variant as a
 * parameter, which is what lets a collection grid use it. Klaviyo's own dynamic button cannot:
 * it resolves the product from `location.pathname` and places a single `#klaviyo-bis-button-container`
 * per page, so it only ever works on a PDP. See blocks/buy-buttons.liquid for the PDP trigger.
 */
const KLAVIYO_ENDPOINT = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';

/** Marketing consent is a separate opt-in — a back-in-stock subscription is not consent to market. */
const KLAVIYO_SUBSCRIPTIONS_ENDPOINT = 'https://a.klaviyo.com/client/subscriptions/';

/** Shopify variants are addressed in Klaviyo's catalog with this composite id. */
const catalogVariantId = (variantId) => `$shopify:::$default:::${variantId}`;

/**
 * A dialog that subscribes a shopper to a back-in-stock notification for one variant.
 *
 * Rendered once per page (see snippets/back-in-stock-modal.liquid) and opened by any number of
 * product cards, each passing its own variant through `data-variant-id`.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLFormElement} form - The subscription form.
 * @property {HTMLInputElement} emailInput - The email field.
 * @property {HTMLButtonElement} submitButton - The submit button.
 * @property {HTMLElement} status - The live region for success and error messages.
 * @property {HTMLElement} [productTitle] - Where the product name is written.
 * @property {HTMLInputElement} [marketingConsent] - Optional marketing opt-in, rendered only when a list is configured.
 *
 * @extends DialogComponent
 */
export class BackInStockDialog extends DialogComponent {
  requiredRefs = ['dialog', 'form', 'emailInput', 'submitButton', 'status'];

  /** @type {string | null} */
  #variantId = null;

  #submitting = false;

  get #companyId() {
    return this.dataset.companyId?.trim() || '';
  }

  get #revision() {
    return this.dataset.revision?.trim() || '2026-07-15';
  }

  /**
   * Opens the dialog for the variant named by the clicked trigger.
   *
   * @param {Event} event - The click event, targeting the element carrying `data-variant-id`.
   */
  openFromTrigger = (event) => {
    const { target } = event;
    const trigger = target instanceof Element ? target.closest('[data-variant-id]') : null;

    if (!(trigger instanceof HTMLElement)) return;

    this.#variantId = trigger.dataset.variantId ?? null;
    if (!this.#variantId) return;

    const { productTitle } = this.refs;
    if (productTitle) productTitle.textContent = trigger.dataset.productTitle ?? '';

    this.#reset();
    this.showDialog();

    // Defer so the dialog has been promoted to the top layer before we move focus into it.
    // preventScroll: on a short viewport the card can scroll, and focus must not nudge it.
    requestAnimationFrame(() => this.refs.emailInput.focus({ preventScroll: true }));
  };

  /**
   * Subscribes the shopper, then reports the outcome in place.
   *
   * @param {SubmitEvent} event - The form submit event.
   */
  handleSubmit = async (event) => {
    event.preventDefault();

    if (this.#submitting) return;

    const { form, emailInput } = this.refs;

    if (!form.checkValidity()) {
      this.#setStatus('error', this.dataset.emailInvalidText);
      emailInput.focus();
      return;
    }

    if (!this.#companyId || !this.#variantId) {
      console.warn('Back in stock: missing Klaviyo public API key or variant id.');
      this.#setStatus('error', this.dataset.errorText);
      return;
    }

    this.#setSubmitting(true);

    try {
      const response = await fetch(`${KLAVIYO_ENDPOINT}?company_id=${encodeURIComponent(this.#companyId)}`, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          revision: this.#revision,
        },
        body: JSON.stringify({
          data: {
            type: 'back-in-stock-subscription',
            attributes: {
              channels: ['EMAIL'],
              profile: {
                data: {
                  type: 'profile',
                  attributes: { email: emailInput.value.trim() },
                },
              },
            },
            relationships: {
              variant: {
                data: {
                  type: 'catalog-variant',
                  id: catalogVariantId(this.#variantId),
                },
              },
            },
          },
        }),
      });

      if (!response.ok) throw new Error(`Klaviyo responded with ${response.status}`);

      // Only after the notification itself is secured, and never as a side effect of it.
      if (this.#marketingOptedIn) await this.#subscribeToMarketing(emailInput.value.trim());

      this.dataset.state = 'success';
      this.#setStatus('success', this.dataset.successText);
    } catch (error) {
      console.error(error);
      this.#setStatus('error', this.dataset.errorText);
    } finally {
      this.#setSubmitting(false);
    }
  };

  /** Whether the shopper ticked the optional marketing opt-in, which only renders when configured. */
  get #marketingOptedIn() {
    const { marketingConsent } = this.refs;

    return marketingConsent instanceof HTMLInputElement && marketingConsent.checked;
  }

  /**
   * Adds the shopper to the configured marketing list. A failure here must not be reported as a
   * failure of the back-in-stock signup, which has already succeeded by this point.
   *
   * @param {string} email - The address to subscribe.
   */
  async #subscribeToMarketing(email) {
    const listId = this.dataset.marketingListId?.trim();

    if (!listId) return;

    try {
      const response = await fetch(
        `${KLAVIYO_SUBSCRIPTIONS_ENDPOINT}?company_id=${encodeURIComponent(this.#companyId)}`,
        {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
            revision: this.#revision,
          },
          body: JSON.stringify({
            data: {
              type: 'subscription',
              attributes: {
                custom_source: 'Back in stock form',
                profile: {
                  data: {
                    type: 'profile',
                    attributes: {
                      email,
                      subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
                    },
                  },
                },
              },
              relationships: {
                list: { data: { type: 'list', id: listId } },
              },
            },
          }),
        }
      );

      if (!response.ok) throw new Error(`Klaviyo responded with ${response.status}`);
    } catch (error) {
      console.error('Back in stock: marketing opt-in failed.', error);
    }
  }

  /**
   * @param {'error' | 'success'} state
   * @param {string} [message]
   */
  #setStatus(state, message) {
    const { status } = this.refs;

    status.dataset.state = state;
    status.textContent = message ?? '';
    status.hidden = !message;
  }

  /** @param {boolean} submitting */
  #setSubmitting(submitting) {
    this.#submitting = submitting;
    this.refs.submitButton.disabled = submitting;
    this.refs.form.setAttribute('aria-busy', String(submitting));
  }

  /** Returns the dialog to its pristine state so a second card does not inherit the first result. */
  #reset() {
    const { form, status } = this.refs;

    form.reset();
    delete this.dataset.state;
    status.hidden = true;
    status.textContent = '';
    delete status.dataset.state;
    this.#setSubmitting(false);
  }
}

if (!customElements.get('back-in-stock-dialog')) {
  customElements.define('back-in-stock-dialog', BackInStockDialog);
}
